import { DateTime, type StringUnitLength } from 'luxon';
import { formatShortRelativeTime } from './formatShortRelativeTime';

export type DateTimeLike = string | number | Date | DateTime;

export function parseDateTime(input: DateTimeLike, zone?: string): DateTime {
  if (DateTime.isDateTime(input)) {
    return zone ? input.setZone(zone) : input;
  }
  if (typeof input === 'string') {
    return DateTime.fromISO(input, { zone });
  }
  if (typeof input === 'number') {
    return DateTime.fromMillis(input, { zone });
  }
  if (input instanceof Date) {
    return DateTime.fromJSDate(input, { zone });
  }
  throw new Error('Invalid date');
}

export const parseDateTimeSafe = (input: unknown, zone?: string): DateTime | null => {
  try {
    return parseDateTime(input as DateTimeLike, zone);
  } catch {
    return null;
  }
};

export const getRelativeTime = (
  time: DateTimeLike,
  baseTime?: DateTimeLike,
  size: StringUnitLength = 'long',
) => {
  const targetDateTime = parseDateTime(time);
  const baseDateTime = baseTime ? parseDateTime(baseTime) : DateTime.now();
  const diffSec = targetDateTime.diff(baseDateTime, 'seconds').seconds;

  if (Math.abs(diffSec) < 60) {
    return formatShortRelativeTime(diffSec, size);
  }
  return (
    targetDateTime
      .toRelative({
        base: baseDateTime,
        style: size,
      })
      ?.replace(' ago', '') || ''
  );
};
